import type { UseHeadInput, UseSeoMetaInput } from '@unhead/vue'
import type { Article, ContentBlock, Image, Team } from '~/types'
import type { PageMeta } from '#app'
import { PageType } from '~/enums/page-type'
import { useConfigStore } from '~/store/useConfigStore'

export const useArticle = (data: MaybeRef<Maybe<Article.Model | undefined>>) => {
  const { image: defaultImage } = useDefaultImage()

  const configStore = useConfigStore()
  const { defaultTimezone } = storeToRefs(configStore)

  const article = computed<Maybe<Article.Model>>(() => unref(data) || null)
  const video = computed<Maybe<Article.Video>>(() => unref(data)?.video || null)
  const id = computed<Maybe<number>>(() => article.value?.id || null)
  const hasContentBlocks = computed<boolean>(() => contentBlocks.value?.length > 0)
  const showAuthor = computed<boolean>(() => !!article.value?.showAuthor)
  const shortDescription = computed<Maybe<string>>(() => article.value?.shortDescription || null)
  const authorFirstName = computed<Maybe<string>>(() => article.value?.author?.firstName || null)
  const authorLastName = computed<Maybe<string>>(() => article.value?.author?.lastName || null)
  const authorFullName = computed<Maybe<string>>(() => authorFirstName.value && authorLastName ? `${authorFirstName.value} ${authorLastName.value}` : null)
  const author = computed<Maybe<Article.Author>>(() => article.value?.author || null)
  const mainImage = computed<Image>(() => article.value?.mainImage || defaultImage.value)
  const publishedAt = computed<Maybe<string>>(() => article.value?.publishedAt || null)
  const { dateLuxonToFormat: articleDate } = useDate(publishedAt, undefined, defaultTimezone.value)
  const title = computed<Maybe<string>>(() => article.value?.title || null)
  const type = computed<PageType>(() => article.value?.type || PageType.Default)

  const videoImage = computed<Maybe<Image>>(() => {
    if (mainImage.value) {
      return mainImage.value
    }

    if (video.value?.poster) {
      return video.value.poster
    }

    return null
  })
  const contentBlocks = computed<ContentBlock.Model[]>(() => {
    return article.value?.contentBlocks || []
  })
  const permalink = computed<Maybe<string>>(() => {
    return article.value?.permalink || null
  })

  const headInput = computed<UseHeadInput>(() => {
    if (!article.value) {
      return {}
    }

    return {
      title: article.value?.title,
    }
  })

  const seoMetaInput = computed<UseSeoMetaInput>(() => (
    {
      title: article.value?.title,
      ogTitle: article.value?.metaTitle || article.value?.title,
      description: article.value?.shortDescription,
      ogDescription: article.value?.metaDescription || article.value?.shortDescription,
      ogImage: article.value?.mainImage?.src,
    }
  ))

  const categories = computed<Article.Category[]>(() => article.value?.categories || [])

  const team = computed<Maybe<Team.Model>>(() => article.value?.teams?.[0] || null)
  const layoutKey = computed<PageMeta['layout']>(() => team.value ? 'team' : 'default')

  const isVideo = computed<boolean>(() => type.value === PageType.Video)
  const isGallery = computed<boolean>(() => type.value === PageType.Gallery)
  const isPage = computed<boolean>(() => type.value === PageType.Page)
  const isDefaultArticle = computed<boolean>(() => type.value === PageType.Default)

  const dashboardUrl = computed<string>(() => {
    switch (type.value) {
      case PageType.Video:
        return `resources/video-articles/${id.value}`
      case PageType.Gallery:
        return `resources/gallery-articles/${id.value}`
      case PageType.Page:
        return `resources/pages/${id.value}`
      default:
        return `resources/articles/${id.value}`
    }
  })

  const dashboardTitle = computed<Maybe<string>>(() => {
    let articleTypeTitle = ''

    switch (type.value) {
      case PageType.Default:
        articleTypeTitle = 'Article'
        break
      case PageType.Video:
        articleTypeTitle = 'Video Article'
        break
      case PageType.Gallery:
        articleTypeTitle = 'Gallery Article'
        break
      case PageType.Page:
        articleTypeTitle = 'Page'
        break
    }

    return title.value || articleTypeTitle
  })

  return {
    article,
    author,
    authorFirstName,
    authorFullName,
    authorLastName,
    categories,
    contentBlocks,
    dashboardUrl,
    dashboardTitle,
    hasContentBlocks,
    headInput,
    id,
    isDefaultArticle,
    isGallery,
    isPage,
    isVideo,
    layoutKey,
    mainImage,
    permalink,
    publishedAt,
    articleDate,
    seoMetaInput,
    shortDescription,
    showAuthor,
    team,
    title,
    type,
    video,
    videoImage,
  }
}
